import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Figma } from 'lucide-react';
import { Reveal, WordReveal } from '../components/Motion';
import { caseStudyBySlug, caseStudies } from '../mock/case-studies';

const SECTION_BG = { background: 'var(--bg-2)' };
const CARD_BG = { background: 'rgba(255,255,255,0.03)', border: '1px solid var(--line)' };
const OUTCOME_BG = { background: 'rgba(199,255,85,0.04)', border: '1px solid rgba(199,255,85,0.18)' };
const NEXT_OVERLAY = { background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.55))' };
const BLOB_STYLE = { width: 500, height: 500, background: 'var(--accent)', top: '-20%', right: '-10%', opacity: 0.12 };
const HERO_DOT = { background: 'var(--accent)' };
const LINE_BORDER = { borderColor: 'var(--line-strong)' };

const Pill = ({ label, value }) => (
  <div>
    <div className="text-dim text-[11px] uppercase tracking-widest">{label}</div>
    <div className="font-display mt-2 text-[15px]">{value}</div>
  </div>
);

const SectionTitle = ({ eyebrow, title }) => (
  <div className="mb-10">
    <span className="section-eyebrow">{eyebrow}</span>
    <h2 className="h-display mt-5 text-3xl md:text-5xl">{title}</h2>
  </div>
);

const Bullet = ({ children }) => (
  <li className="flex gap-3 text-[14.5px] text-muted leading-relaxed">
    <span className="mt-2.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={HERO_DOT} />
    <span>{children}</span>
  </li>
);

const ProjectHero = ({ study }) => (
  <section className="relative pt-32 pb-16 px-6 md:px-12 lg:px-20 overflow-hidden">
    <div className="blob" style={BLOB_STYLE} />
    <div className="max-w-[1500px] mx-auto relative">
      <Link to="/ux-folio" className="text-[12px] uppercase tracking-widest text-dim inline-flex items-center gap-2 hover:text-accent">
        <ArrowLeft size={13} /> All case studies
      </Link>
      <div className="mt-10 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-8">
          <div className="text-accent text-[12px] uppercase tracking-widest">{study.industry} • {study.year}</div>
          <h1 className="h-display mt-5 text-5xl md:text-7xl leading-[1.02]">
            <WordReveal text={study.title} />
          </h1>
          <p className="mt-6 font-serif-i text-2xl md:text-3xl text-muted">{study.tagline}</p>
        </div>
        <div className="md:col-span-4 card-prem p-6 grid grid-cols-2 gap-x-5 gap-y-5">
          <Pill label="Role" value={study.role} />
          <Pill label="Duration" value={study.duration} />
          <Pill label="Team" value={study.team} />
          <Pill label="Platform" value={study.platform} />
        
 
        </div>
      </div>
      <div className="mt-14 aspect-[16/8] rounded-2xl overflow-hidden ring-glow">
        <img src={study.cover} alt={study.title} className="w-full h-full " />
      </div>
    </div>
  </section>
);

const OverviewProblem = ({ overview, problem }) => (
  <section className="px-6 md:px-12 lg:px-20 py-24">
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
      <Reveal className="md:col-span-6">
        <SectionTitle eyebrow="Overview" title="What & why." />
        <p className="text-muted text-[15.5px] leading-[1.85]">{overview}</p>
      </Reveal>
      <Reveal className="md:col-span-6" delay={0.1}>
        <SectionTitle eyebrow="Problem" title="The brief." />
        <p className="text-muted text-[15.5px] leading-[1.85]">{problem}</p>
      </Reveal>
    </div>
  </section>
);

const InsightItem = ({ index, text }) => (
  <li className="flex gap-4 p-5 rounded-xl" style={CARD_BG}>
    <span className="font-display text-accent text-xl mt-px">{String(index + 1).padStart(2, '0')}</span>
    <span className="text-[15px] leading-relaxed">{text}</span>
  </li>
);

const ResearchInsights = ({ research, insights }) => {
  if (!research && (!insights || insights.length === 0)) return null;
  return (
    <section className="px-6 md:px-12 lg:px-20 py-24" style={SECTION_BG}>
      <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
        {research && (
          <Reveal className="md:col-span-5">
            <SectionTitle eyebrow="Research" title="Discovery." />
            <p className="text-muted text-[15.5px] leading-[1.85]">{research}</p>
          </Reveal>
        )}
        {insights && insights.length > 0 && (
          <Reveal className="md:col-span-7" delay={0.1}>
            <SectionTitle eyebrow="User insights" title="What we learned." />
            <ul className="space-y-4">
              {insights.map((it, i) => <InsightItem key={`i${i}`} index={i} text={it} />)}
            </ul>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const ProcessCard = ({ index, step, body }) => (
  <Reveal delay={index * 0.05}>
    <div className="card-prem p-7 h-full">
      <div className="text-dim font-display text-sm">0{index + 1}</div>
      <h3 className="font-display text-2xl mt-6">{step}</h3>
      <p className="text-muted text-[14px] mt-3 leading-relaxed">{body}</p>
    </div>
  </Reveal>
);

const Process = ({ process }) => {
  if (!process || process.length === 0) return null;
  return (
    <section className="px-6 md:px-12 lg:px-20 py-24">
      <div className="max-w-[1500px] mx-auto">
        <SectionTitle eyebrow="Design process" title="How it came together." />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {process.map((p, i) => <ProcessCard key={`p${i}`} index={i} step={p.step} body={p.body} />)}
        </div>
      </div>
    </section>
  );
};

const renderImage = (item, index, fallback) => {
  const src = typeof item === 'string' ? item : item.src;
  const alt = typeof item === 'string'
    ? fallback
    : (item.alt || item.caption || fallback);
  const caption  = typeof item === 'string' ? null : item.caption;
  const maxWidth = typeof item === 'string' ? null : item.maxWidth; // ← new

  const figureStyle = maxWidth
    ? { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
        width: '100%',
        alignSelf: 'center',
        marginLeft: 'auto',
        marginRight: 'auto',
        justifySelf: 'center' }
    : undefined;

  return (
    <figure key={index} className="case-study-showcase" style={figureStyle}>
      <img src={src} alt={alt} loading="lazy" className="case-study-showcase__img" />
      {caption && <figcaption className="case-study-showcase__caption">{caption}</figcaption>}
    </figure>
  );
};

const GalleryItem = ({ item, index }) => {
  // Array of two = 2-column row
  if (Array.isArray(item)) {
    return (
      <Reveal delay={index * 0.05}>
        <div className="case-study-showcase-row">
          {item.map((sub, i) => renderImage(sub, i, `Showcase ${index + 1}.${i + 1}`))}
        </div>
      </Reveal>
    );
  }
  // Single item = full width
  return (
    <Reveal delay={index * 0.05}>
      {renderImage(item, index, `Showcase ${index + 1}`)}
    </Reveal>
  );
};

const Gallery = ({ gallery }) => {
  if (!gallery) return null;
  const images = Array.isArray(gallery) ? gallery : [gallery];
  if (images.length === 0) return null;

  return (
    <section className="px-6 md:px-12 lg:px-20 py-24" data-testid="case-study-gallery">
      <div className="max-w-[1500px] mx-auto">
        <SectionTitle eyebrow="Showcase" title="In the wild." />
        <div className="case-study-showcase-stack">
          {images.map((item, index) => (
            <GalleryItem key={index} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

const DecisionsChallenges = ({ decisions, challenges }) => {
  const hasDecisions = decisions && decisions.length > 0;
  const hasChallenges = challenges && challenges.length > 0;
  if (!hasDecisions && !hasChallenges) return null;
  return (
    <section className="px-6 md:px-12 lg:px-20 py-24" style={SECTION_BG}>
      <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
        {hasDecisions && (
          <Reveal className="md:col-span-6">
            <SectionTitle eyebrow="Key decisions" title="Bets we made." />
            <ul className="space-y-3">
              {decisions.map((d, i) => <Bullet key={`d${i}`}>{d}</Bullet>)}
            </ul>
          </Reveal>
        )}
        {hasChallenges && (
          <Reveal className="md:col-span-6" delay={0.1}>
            <SectionTitle eyebrow="Challenges" title="What we wrestled with." />
            <ul className="space-y-3">
              {challenges.map((c, i) => <Bullet key={`c${i}`}>{c}</Bullet>)}
            </ul>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const PrototypeBlock = ({ figmaUrl }) => {
  if (!figmaUrl || figmaUrl === '#') return null;
  return (
    <section className="px-6 md:px-12 lg:px-20 py-24">
      <div className="max-w-[1500px] mx-auto card-prem p-10 grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-8">
          <span className="section-eyebrow">Prototype</span>
          <h3 className="h-display mt-5 text-3xl md:text-4xl">See it in Figma.</h3>
          <p className="text-muted mt-4 max-w-xl">
            Open the interactive prototype to walk through the flows, components and motion specs in their native environment.
          </p>
        </div>
        <div className="md:col-span-4 flex flex-wrap gap-3 md:justify-end">
          <a href={figmaUrl} target="_blank" rel="noreferrer" className="btn-pill solid"><Figma size={14} /> Open Figma</a>
        </div>
      </div>
    </section>
  );
};

const OutcomeCard = ({ text }) => (
  <div className="p-5 rounded-xl" style={OUTCOME_BG}>
    <span className="text-accent font-display text-xl">✔</span>
    <span className="ml-3 text-[15px] leading-relaxed">{text}</span>
  </div>
);

const OutcomesLearnings = ({ outcomes, learnings }) => {
  const hasOutcomes = outcomes && outcomes.length > 0;
  const hasLearnings = learnings && learnings.length > 0;
  if (!hasOutcomes && !hasLearnings) return null;
  return (
    <section className="px-6 md:px-12 lg:px-20 py-24" style={SECTION_BG}>
      <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
        {hasOutcomes && (
          <Reveal className="md:col-span-6">
            <SectionTitle eyebrow="Outcomes" title="What changed." />
            <div className="space-y-4">
              {outcomes.map((o, i) => <OutcomeCard key={`o${i}`} text={o} />)}
            </div>
          </Reveal>
        )}
        {hasLearnings && (
          <Reveal className="md:col-span-6" delay={0.1}>
            <SectionTitle eyebrow="Learnings" title="What I took away." />
            <ul className="space-y-3">
              {learnings.map((l, i) => <Bullet key={`l${i}`}>{l}</Bullet>)}
            </ul>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const Swatch = ({ c }) => (
  <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--line)' }}>
    <div style={{ background: c.hex, height: 96 }} />
    <div className="p-3">
      <div className="text-[11px] uppercase tracking-widest text-accent">{c.name}</div>
      <div className="text-[12.5px] mt-1 font-mono">{c.hex}</div>
      {c.role && <div className="text-muted text-[11.5px] mt-1">{c.role}</div>}
      {c.contrast && (
        <div className="mt-2 flex gap-1.5">
          {c.contrast.map((b, i) => (
            <span key={i} className="text-[9.5px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'rgba(199,255,85,0.10)', color: 'var(--accent)' }}>{b}</span>
          ))}
        </div>
      )}
    </div>
  </div>
);

const ColorFamily = ({ family }) => (
  <div>
    <div className="flex items-baseline justify-between mb-3">
      <div className="text-[12px] uppercase tracking-widest text-accent">{family.name}</div>
      <div className="text-[11px] text-dim">{family.desc}</div>
    </div>
    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
      {family.colors.map((c) => <Swatch key={c.name} c={c} />)}
    </div>
  </div>
);

const TypeRow = ({ t }) => (
  <div className="py-6 border-t first:border-t-0" style={{ borderColor: 'var(--line)' }}>
    <div className="grid md:grid-cols-12 gap-6 items-baseline">
      <div className="md:col-span-3">
        <div className="text-[12px] uppercase tracking-widest text-accent">{t.name}</div>
        <div className="text-[11px] text-dim font-mono mt-1">{t.size} · {t.weight} · {t.lh}</div>
        {t.tracking && <div className="text-[11px] text-dim font-mono">tracking {t.tracking}</div>}
      </div>
      <div className="md:col-span-9">
        <div style={{
          fontFamily: t.family, fontSize: t.previewSize || t.size,
          fontWeight: t.previewWeight || t.weight, lineHeight: 1.15, letterSpacing: t.tracking || 'normal',
        }}>
          {t.sample || 'The quick brown fox jumps'}
        </div>
      </div>
    </div>
  </div>
);

const StateRow = ({ label, states }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest text-dim mb-3">{label}</div>
    <div className="flex flex-wrap gap-3">
      {states.map((s) => (
        <div key={s.name} className="px-4 py-2 rounded-lg text-[13px] font-medium" style={s.style}>
          {s.name}
        </div>
      ))}
    </div>
  </div>
);

const DesignSystem = ({ ds }) => {
  if (!ds) return null;
  return (
    <section className="px-6 md:px-12 lg:px-20 py-24" data-testid="case-study-design-system">
      <div className="max-w-[1500px] mx-auto">
        <SectionTitle eyebrow="Design System" title="Foundations that scale." />

        {/* Overview strip */}
        {ds.overview && (
          <p className="text-muted text-[15.5px] leading-relaxed max-w-3xl mb-14">{ds.overview}</p>
        )}

        {/* KEY STATS */}
        {ds.stats?.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
            {ds.stats.map((s) => (
              <div key={s.label} className="card-prem p-6">
                <div className="h-display text-4xl md:text-5xl text-accent">{s.value}</div>
                <div className="text-[11.5px] uppercase tracking-widest text-dim mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* COLOUR FAMILIES */}
        {ds.colorFamilies?.length > 0 && (
          <div className="card-prem p-8 md:p-10 mb-6">
            <h4 className="h-display text-2xl">Colour system</h4>
            <p className="text-muted text-[13.5px] mt-2 max-w-2xl">
              Semantic tokens layered over a primitive palette — every colour has a job.
            </p>
            <div className="mt-8 space-y-10">
              {ds.colorFamilies.map((f) => <ColorFamily key={f.name} family={f} />)}
            </div>
          </div>
        )}

        {/* TYPOGRAPHY */}
        {ds.typography?.length > 0 && (
          <div className="card-prem p-8 md:p-10 mb-6">
            <div className="flex items-baseline justify-between gap-6 flex-wrap">
              <div>
                <h4 className="h-display text-2xl">Typography scale</h4>
                <p className="text-muted text-[13.5px] mt-2 max-w-2xl">
                  A single family, a strict scale, a defined line-height ramp.
                </p>
              </div>
              {ds.typeFamily && (
                <div className="text-[12px] font-mono text-dim">{ds.typeFamily}</div>
              )}
            </div>
            <div className="mt-6">
              {ds.typography.map((t) => <TypeRow key={t.name} t={t} />)}
            </div>
          </div>
        )}

        {/* SPACING + RADII + ELEVATION */}
        <div className="grid md:grid-cols-12 gap-6 mb-6">
          {ds.spacing?.length > 0 && (
            <div className="md:col-span-5 card-prem p-8">
              <h4 className="h-display text-xl">Spacing (4pt grid)</h4>
              <div className="mt-6 space-y-3">
                {ds.spacing.map((s) => (
                  <div key={s.token} className="flex items-center gap-4">
                    <div className="text-[11.5px] text-dim font-mono w-24">{s.token}</div>
                    <div style={{ background: 'var(--accent)', height: 8, width: s.px, borderRadius: 4 }} />
                    <div className="text-[12px] text-muted font-mono">{s.px}px</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ds.radii?.length > 0 && (
            <div className="md:col-span-4 card-prem p-8">
              <h4 className="h-display text-xl">Corner radii</h4>
              <div className="mt-6 grid grid-cols-2 gap-4">
                {ds.radii.map((r) => (
                  <div key={r.token} className="flex flex-col items-center gap-2">
                    <div className="w-full aspect-square border"
                      style={{ borderColor: 'var(--line)', borderRadius: r.px, background: 'rgba(199,255,85,0.06)' }} />
                    <div className="text-[11px] text-dim font-mono">{r.token} · {r.px}px</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ds.elevation?.length > 0 && (
            <div className="md:col-span-3 card-prem p-8">
              <h4 className="h-display text-xl">Elevation</h4>
              <div className="mt-6 space-y-4">
                {ds.elevation.map((e) => (
                  <div key={e.token} className="p-4 rounded-xl flex items-center justify-between"
                    style={{ background: 'rgba(255,255,255,0.06)', boxShadow: e.shadow }}>
                    <div className="text-[12px] text-muted">{e.token}</div>
                    <div className="text-[10px] text-dim font-mono">z{e.z}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* GRID + BREAKPOINTS + MOTION */}
        <div className="grid md:grid-cols-12 gap-6 mb-6">
          {ds.grid && (
            <div className="md:col-span-4 card-prem p-8">
              <h4 className="h-display text-xl">Layout grid</h4>
              <div className="mt-6 space-y-2 text-[13px] text-muted font-mono">
                <div>Columns · {ds.grid.columns}</div>
                <div>Gutter · {ds.grid.gutter}</div>
                <div>Margin · {ds.grid.margin}</div>
                <div>Max width · {ds.grid.maxWidth}</div>
              </div>
            </div>
          )}

          {ds.breakpoints?.length > 0 && (
            <div className="md:col-span-4 card-prem p-8">
              <h4 className="h-display text-xl">Breakpoints</h4>
              <div className="mt-6 space-y-2 text-[13px] text-muted font-mono">
                {ds.breakpoints.map((b) => (
                  <div key={b.name} className="flex justify-between">
                    <span className="text-accent">{b.name}</span>
                    <span>{b.min}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ds.motion?.length > 0 && (
            <div className="md:col-span-4 card-prem p-8">
              <h4 className="h-display text-xl">Motion tokens</h4>
              <div className="mt-6 space-y-2 text-[13px] text-muted font-mono">
                {ds.motion.map((m) => (
                  <div key={m.token} className="flex justify-between gap-3">
                    <span className="text-accent">{m.token}</span>
                    <span className="truncate">{m.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* BUTTON STATES DEMO */}
        {ds.buttonStates?.length > 0 && (
          <div className="card-prem p-8 md:p-10 mb-6">
            <h4 className="h-display text-2xl">Button states</h4>
            <div className="mt-8 space-y-6">
              {ds.buttonStates.map((row) => <StateRow key={row.label} label={row.label} states={row.states} />)}
            </div>
          </div>
        )}

        {/* COMPONENT LIBRARY */}
        {ds.components?.length > 0 && (
          <div className="card-prem p-8 md:p-10 mb-6">
            <div className="flex items-baseline justify-between gap-6 flex-wrap">
              <h4 className="h-display text-2xl">Component library</h4>
              <div className="text-[11px] uppercase tracking-widest text-dim">
                {ds.components.length} components · {ds.components.reduce((s, c) => s + (c.variants || 0), 0)} variants total
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
              {ds.components.map((c) => (
                <div key={c.name} className="p-6 rounded-2xl border transition hover:border-accent/60"
                  style={{ borderColor: 'var(--line)', background: 'rgba(255,255,255,0.02)' }}>
                  <div className="flex items-baseline justify-between">
                    <div className="text-[12px] uppercase tracking-widest text-accent">{c.name}</div>
                    {c.variants && <div className="text-[11px] text-dim font-mono">{c.variants}v</div>}
                  </div>
                  <p className="text-muted text-[13px] mt-2 leading-relaxed">{c.desc}</p>
                  {c.props && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {c.props.map((p) => (
                        <span key={p} className="text-[10.5px] px-2 py-0.5 rounded font-mono"
                          style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--fg-muted)' }}>{p}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRINCIPLES */}
        {ds.principles?.length > 0 && (
          <div className="card-prem p-8 md:p-10">
            <h4 className="h-display text-2xl">Design principles</h4>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {ds.principles.map((p, i) => (
                <div key={p.title} className="p-5 rounded-xl border" style={{ borderColor: 'var(--line)' }}>
                  <div className="h-display text-3xl text-accent">0{i + 1}</div>
                  <div className="text-[13px] uppercase tracking-widest mt-3">{p.title}</div>
                  <p className="text-muted text-[13.5px] mt-2 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const NextCaseStudy = ({ next }) => (
  <section className="px-6 md:px-12 lg:px-20 py-24">
    <div className="max-w-[1500px] mx-auto">
      <span className="section-eyebrow">Next case study</span>
      <Link to={`/work/${next.slug}`} className="mt-6 group block card-prem overflow-hidden" data-cursor="hover">
        <div className="grid md:grid-cols-12 gap-0 items-stretch">
          <div className="md:col-span-5 aspect-[16/10] md:aspect-auto overflow-hidden relative">
            <img src={next.cover} alt={next.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1400ms]" />
            <div className="absolute inset-0" style={NEXT_OVERLAY} />
          </div>
          <div className="md:col-span-7 p-10 flex flex-col justify-center">
            <div className="text-accent text-[12px] uppercase tracking-widest">{next.industry}</div>
            <h3 className="h-display text-3xl md:text-5xl mt-4">{next.title}</h3>
            <p className="text-muted mt-4 max-w-md">{next.tagline}</p>
            <span className="link-arrow mt-8">Read case study <span className="arrow"><ArrowUpRight size={14} /></span></span>
          </div>
        </div>
      </Link>
    </div>
  </section>
);

const getNextStudy = (slug) => {
  const all = Object.values(caseStudies);
  const idx = all.findIndex((c) => c.slug === slug);
  return all[(idx + 1) % all.length];
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const study = caseStudyBySlug(slug);
  if (!study) return <Navigate to="/ux-folio" replace />;
  const next = getNextStudy(slug);

  return (
    <div className="relative">
      <ProjectHero study={study} />
      <OverviewProblem overview={study.overview} problem={study.problem} />
      <ResearchInsights research={study.research} insights={study.insights} />
      <Process process={study.process} />
      <DesignSystem ds={study.designSystem} />
      <DecisionsChallenges decisions={study.decisions} challenges={study.challenges} />
      <Gallery gallery={study.gallery} />
      <PrototypeBlock figmaUrl={study.figmaUrl} />
      <OutcomesLearnings outcomes={study.outcomes} learnings={study.learnings} />
      <NextCaseStudy next={next} />
    </div>
  );
}